/**
 * useDiscoveryFiltersState - Single source of truth for discovery filter state
 *
 * Manages:
 * - Taxonomy path (industry → category → subcategory)
 * - Range filters (commission, price, discount)
 * - Draft vs committed state separation
 * - State persistence across open/close
 *
 * Rule: Typing or opening UI never reframes the feed.
 * Only explicit actions (Apply, mini suggestion click, Search CTA, deepest headline) commit changes.
 *
 * @version 1.0.0
 */
import { useState, useCallback, useMemo, useEffect } from 'react';

export interface RangeFilter {
  min: number | null;
  max: number | null;
}

export interface DiscoveryFiltersState {
  // Taxonomy path
  industryIds: string[];
  categoryIds: string[];
  subcategoryIds: string[];

  // Range filters
  commissionRange: RangeFilter;
  priceRange: RangeFilter;
  discountRange: RangeFilter;

  // Search query
  searchQuery: string;
}

export interface DiscoveryFiltersDraft extends DiscoveryFiltersState {
  // Draft-only fields
  draftQuery: string;
}

// Default ranges: null means "unset" = use bounds
// Chips ONLY render when values are NOT null
const DEFAULT_COMMISSION_RANGE: RangeFilter = { min: null, max: null };
const DEFAULT_PRICE_RANGE: RangeFilter = { min: null, max: null };
const DEFAULT_DISCOUNT_RANGE: RangeFilter = { min: null, max: null };

const createDefaultFilters = (): DiscoveryFiltersState => ({
  industryIds: [],
  categoryIds: [],
  subcategoryIds: [],
  commissionRange: DEFAULT_COMMISSION_RANGE,
  priceRange: DEFAULT_PRICE_RANGE,
  discountRange: DEFAULT_DISCOUNT_RANGE,
  searchQuery: '',
});

export interface UseDiscoveryFiltersStateReturn {
  // Committed state (drives feed)
  committed: DiscoveryFiltersState;

  // Draft state (for UI editing without reframing)
  draft: DiscoveryFiltersDraft;

  // Draft setters (do NOT reframe feed)
  setDraftQuery: (query: string) => void;
  addDraftIndustry: (id: string) => void;
  removeDraftIndustry: (id: string) => void;
  addDraftCategory: (id: string) => void;
  removeDraftCategory: (id: string) => void;
  addDraftSubcategory: (id: string) => void;
  removeDraftSubcategory: (id: string) => void;
  setDraftCommissionRange: (range: RangeFilter) => void;
  setDraftPriceRange: (range: RangeFilter) => void;
  setDraftDiscountRange: (range: RangeFilter) => void;

  // Validity-based cleanup for taxonomy selections
  // Call this after industries/categories change with current valid facet IDs
  cleanupInvalidCategories: (validCategoryIds: string[]) => void;
  cleanupInvalidSubcategories: (validSubcategoryIds: string[]) => void;

  // Commit actions (reframe feed)
  commitAll: () => void;
  commitSearch: () => void;
  commitTaxonomy: (
    industryIds?: string[],
    categoryIds?: string[],
    subcategoryIds?: string[],
  ) => void;

  // Clear actions
  clearDraft: () => void;
  clearAll: () => void;

  // Reset draft to committed state
  resetDraftToCommitted: () => void;

  // Check if draft differs from committed
  hasDraftChanges: boolean;

  // Count of active filters
  activeFilterCount: number;

  // Active pills for display
  activePills: ActivePill[];

  // Remove a pill
  removePill: (
    type: 'industry' | 'category' | 'subcategory' | 'commission' | 'price' | 'discount' | 'query',
    id?: string,
  ) => void;
}

export interface ActivePill {
  type: 'industry' | 'category' | 'subcategory' | 'commission' | 'price' | 'discount' | 'query';
  id?: string;
  label: string;
}

export function useDiscoveryFiltersState(
  initialFilters?: Partial<DiscoveryFiltersState>,
): UseDiscoveryFiltersStateReturn {
  // Extract parent values for stable dependency tracking
  const parentSearchQuery = initialFilters?.searchQuery;
  const parentIndustryIds = initialFilters?.industryIds;
  const parentCategoryIds = initialFilters?.categoryIds;
  const parentCommissionRange = initialFilters?.commissionRange;
  const parentPriceRange = initialFilters?.priceRange;
  const parentDiscountRange = initialFilters?.discountRange;

  // Serialize arrays for dependency comparison (since array refs change each render)
  const industryIdsKey = parentIndustryIds?.join(',') ?? '';
  const categoryIdsKey = parentCategoryIds?.join(',') ?? '';

  // Serialize range values for stable dependency tracking
  const commissionRangeKey = `${parentCommissionRange?.min ?? 'null'}-${parentCommissionRange?.max ?? 'null'}`;
  const priceRangeKey = `${parentPriceRange?.min ?? 'null'}-${parentPriceRange?.max ?? 'null'}`;
  const discountRangeKey = `${parentDiscountRange?.min ?? 'null'}-${parentDiscountRange?.max ?? 'null'}`;

  // Committed state - drives the feed
  const [committed, setCommitted] = useState<DiscoveryFiltersState>(() => ({
    ...createDefaultFilters(),
    ...initialFilters,
  }));

  // Draft state - for UI editing
  const [draft, setDraft] = useState<DiscoveryFiltersDraft>(() => ({
    ...committed,
    draftQuery: committed.searchQuery,
  }));

  // SYNC EFFECT: When parent state changes externally, update our internal committed state
  // This ensures the hook reflects the parent's source of truth
  // Use serialized keys for stable dependency tracking (arrays create new refs each render)
  // NOTE: We sync COMMITTED taxonomy + ranges from parent
  // The draft will also be synced to ensure Apply sends current URL values
  useEffect(() => {
    setCommitted((prev) => ({
      ...prev,
      searchQuery: parentSearchQuery ?? prev.searchQuery,
      industryIds: parentIndustryIds ?? prev.industryIds,
      categoryIds: parentCategoryIds ?? prev.categoryIds,
      // Sync range values from parent (URL source of truth)
      commissionRange: parentCommissionRange ?? prev.commissionRange,
      priceRange: parentPriceRange ?? prev.priceRange,
      discountRange: parentDiscountRange ?? prev.discountRange,
    }));

    // Also sync draft taxonomy AND ranges when parent changes, to prevent divergence
    // This ensures that when user opens drawer, draft reflects current URL values
    // CRITICAL: Ranges MUST be synced so Apply preserves existing URL values
    setDraft((prev) => ({
      ...prev,
      industryIds: parentIndustryIds ?? prev.industryIds,
      categoryIds: parentCategoryIds ?? prev.categoryIds,
      // Sync ranges from parent - ensures Apply sends existing URL values if user doesn't change them
      commissionRange: parentCommissionRange ?? prev.commissionRange,
      priceRange: parentPriceRange ?? prev.priceRange,
      discountRange: parentDiscountRange ?? prev.discountRange,
      // Don't sync searchQuery to draftQuery - let user keep typing
    }));
  }, [
    parentSearchQuery,
    industryIdsKey,
    categoryIdsKey,
    parentIndustryIds,
    parentCategoryIds,
    commissionRangeKey,
    priceRangeKey,
    discountRangeKey,
    parentCommissionRange,
    parentPriceRange,
    parentDiscountRange,
  ]);

  // Draft setters
  const setDraftQuery = useCallback((query: string) => {
    setDraft((prev) => ({ ...prev, draftQuery: query }));
  }, []);

  const addDraftIndustry = useCallback((id: string) => {
    setDraft((prev) => {
      if (prev.industryIds.includes(id)) return prev;
      // Simply add the industry, DO NOT clear categories/subcategories
      // Validity check happens externally via derived facets
      return {
        ...prev,
        industryIds: [...prev.industryIds, id],
      };
    });
  }, []);

  const removeDraftIndustry = useCallback((id: string) => {
    setDraft((prev) => {
      const newIndustryIds = prev.industryIds.filter((i) => i !== id);
      // DO NOT clear categories here - validity check happens externally
      return {
        ...prev,
        industryIds: newIndustryIds,
      };
    });
  }, []);

  const addDraftCategory = useCallback((id: string) => {
    setDraft((prev) => {
      if (prev.categoryIds.includes(id)) return prev;
      // Simply add the category, DO NOT clear subcategories
      return {
        ...prev,
        categoryIds: [...prev.categoryIds, id],
      };
    });
  }, []);

  const removeDraftCategory = useCallback((id: string) => {
    setDraft((prev) => {
      // DO NOT clear subcategories here - validity check happens externally
      return {
        ...prev,
        categoryIds: prev.categoryIds.filter((i) => i !== id),
      };
    });
  }, []);

  const addDraftSubcategory = useCallback((id: string) => {
    setDraft((prev) => {
      if (prev.subcategoryIds.includes(id)) return prev;
      return {
        ...prev,
        subcategoryIds: [...prev.subcategoryIds, id],
      };
    });
  }, []);

  const removeDraftSubcategory = useCallback((id: string) => {
    setDraft((prev) => ({
      ...prev,
      subcategoryIds: prev.subcategoryIds.filter((i) => i !== id),
    }));
  }, []);

  const setDraftCommissionRange = useCallback((range: RangeFilter) => {
    setDraft((prev) => ({ ...prev, commissionRange: range }));
  }, []);

  const setDraftPriceRange = useCallback((range: RangeFilter) => {
    setDraft((prev) => ({ ...prev, priceRange: range }));
  }, []);

  const setDraftDiscountRange = useCallback((range: RangeFilter) => {
    setDraft((prev) => ({ ...prev, discountRange: range }));
  }, []);

  // Validity-based cleanup: remove categories that are no longer valid
  const cleanupInvalidCategories = useCallback((validCategoryIds: string[]) => {
    const validSet = new Set(validCategoryIds);
    setDraft((prev) => {
      const filteredCats = prev.categoryIds.filter((id) => validSet.has(id));
      // If no change, return same reference
      if (filteredCats.length === prev.categoryIds.length) return prev;
      return {
        ...prev,
        categoryIds: filteredCats,
      };
    });
  }, []);

  // Validity-based cleanup: remove subcategories that are no longer valid
  const cleanupInvalidSubcategories = useCallback((validSubcategoryIds: string[]) => {
    const validSet = new Set(validSubcategoryIds);
    setDraft((prev) => {
      const filteredSubs = prev.subcategoryIds.filter((id) => validSet.has(id));
      // If no change, return same reference
      if (filteredSubs.length === prev.subcategoryIds.length) return prev;
      return {
        ...prev,
        subcategoryIds: filteredSubs,
      };
    });
  }, []);

  // Commit actions
  const commitAll = useCallback(() => {
    const newCommitted: DiscoveryFiltersState = {
      industryIds: draft.industryIds,
      categoryIds: draft.categoryIds,
      subcategoryIds: draft.subcategoryIds,
      commissionRange: draft.commissionRange,
      priceRange: draft.priceRange,
      discountRange: draft.discountRange,
      searchQuery: draft.draftQuery,
    };
    setCommitted(newCommitted);
    setDraft((prev) => ({ ...prev, ...newCommitted }));
  }, [draft]);

  const commitSearch = useCallback(() => {
    setCommitted((prev) => ({
      ...prev,
      searchQuery: draft.draftQuery,
    }));
    setDraft((prev) => ({ ...prev, searchQuery: draft.draftQuery }));
  }, [draft.draftQuery]);

  const commitTaxonomy = useCallback(
    (industryIds?: string[], categoryIds?: string[], subcategoryIds?: string[]) => {
      setCommitted((prev) => ({
        ...prev,
        industryIds: industryIds ?? draft.industryIds,
        categoryIds: categoryIds ?? draft.categoryIds,
        subcategoryIds: subcategoryIds ?? draft.subcategoryIds,
        searchQuery: draft.draftQuery,
      }));
      setDraft((prev) => ({
        ...prev,
        industryIds: industryIds ?? prev.industryIds,
        categoryIds: categoryIds ?? prev.categoryIds,
        subcategoryIds: subcategoryIds ?? prev.subcategoryIds,
        searchQuery: draft.draftQuery,
      }));
    },
    [draft],
  );

  // Clear actions
  const clearDraft = useCallback(() => {
    const defaultFilters = createDefaultFilters();
    setDraft({
      ...defaultFilters,
      draftQuery: '',
    });
  }, []);

  const clearAll = useCallback(() => {
    const defaultFilters = createDefaultFilters();
    setCommitted(defaultFilters);
    setDraft({
      ...defaultFilters,
      draftQuery: '',
    });
  }, []);

  const resetDraftToCommitted = useCallback(() => {
    setDraft({
      ...committed,
      draftQuery: committed.searchQuery,
    });
  }, [committed]);

  // Compute if draft differs
  const hasDraftChanges = useMemo(() => {
    return (
      draft.draftQuery !== committed.searchQuery ||
      JSON.stringify(draft.industryIds) !== JSON.stringify(committed.industryIds) ||
      JSON.stringify(draft.categoryIds) !== JSON.stringify(committed.categoryIds) ||
      JSON.stringify(draft.subcategoryIds) !== JSON.stringify(committed.subcategoryIds) ||
      draft.commissionRange.min !== committed.commissionRange.min ||
      draft.commissionRange.max !== committed.commissionRange.max ||
      draft.priceRange.min !== committed.priceRange.min ||
      draft.priceRange.max !== committed.priceRange.max ||
      draft.discountRange.min !== committed.discountRange.min ||
      draft.discountRange.max !== committed.discountRange.max
    );
  }, [draft, committed]);

  // Count active filters (using null-based detection)
  // Only count if at least one bound is explicitly set (non-null)
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (committed.industryIds.length > 0) count++;
    if (committed.categoryIds.length > 0) count++;
    if (committed.subcategoryIds.length > 0) count++;
    if (committed.commissionRange.min !== null || committed.commissionRange.max !== null) count++;
    if (committed.priceRange.min !== null || committed.priceRange.max !== null) count++;
    if (committed.discountRange.min !== null || committed.discountRange.max !== null) count++;
    if (committed.searchQuery.trim()) count++;
    return count;
  }, [committed]);

  // Active pills (computed from COMMITTED state for consistency with feed)
  // RULE: Only show pills for values that are EXPLICITLY SET (not null)
  // CRITICAL: Pills must reflect committed state so rail matches feed results
  const activePills = useMemo((): ActivePill[] => {
    const pills: ActivePill[] = [];

    // Query pill first - from committed state
    if (committed.searchQuery.trim()) {
      pills.push({ type: 'query', id: 'search-query', label: committed.searchQuery.trim() });
    }

    // Use ID as label - consumer should override with proper lookup
    committed.industryIds.forEach((id) => {
      pills.push({ type: 'industry', id, label: id });
    });

    committed.categoryIds.forEach((id) => {
      pills.push({ type: 'category', id, label: id });
    });

    committed.subcategoryIds.forEach((id) => {
      pills.push({ type: 'subcategory', id, label: id });
    });

    // All range filters display in DOLLARS
    // Only show chip if at least one bound is explicitly set (non-null)
    if (committed.commissionRange.min !== null || committed.commissionRange.max !== null) {
      const minVal = committed.commissionRange.min ?? 0;
      const maxVal = committed.commissionRange.max ?? 999;
      pills.push({
        type: 'commission',
        label: `$${minVal}-$${maxVal}`,
      });
    }

    if (committed.priceRange.min !== null || committed.priceRange.max !== null) {
      const minVal = committed.priceRange.min ?? 0;
      const maxVal = committed.priceRange.max ?? 9999;
      pills.push({
        type: 'price',
        label: `$${minVal}-$${maxVal}`,
      });
    }

    if (committed.discountRange.min !== null || committed.discountRange.max !== null) {
      const minVal = committed.discountRange.min ?? 0;
      const maxVal = committed.discountRange.max ?? 999;
      pills.push({
        type: 'discount',
        label: `$${minVal}-$${maxVal}`,
      });
    }

    return pills;
  }, [committed]);

  // Remove pill - validity-based cleanup only, not cascade clear
  // NOTE: Commits immediately to sync with feed
  const removePill = useCallback(
    (
      type: 'industry' | 'category' | 'subcategory' | 'commission' | 'price' | 'discount' | 'query',
      id?: string,
    ) => {
      switch (type) {
        case 'query':
          // Clear the search query from both draft and committed
          setDraft((prev) => ({
            ...prev,
            draftQuery: '',
            searchQuery: '',
          }));
          setCommitted((prev) => ({
            ...prev,
            searchQuery: '',
          }));
          break;
        case 'industry':
          // Remove the industry and commit
          if (id) {
            setDraft((prev) => ({
              ...prev,
              industryIds: prev.industryIds.filter((i) => i !== id),
            }));
            setCommitted((prev) => ({
              ...prev,
              industryIds: prev.industryIds.filter((i) => i !== id),
            }));
          }
          break;
        case 'category':
          // Remove the category and commit
          if (id) {
            setDraft((prev) => ({
              ...prev,
              categoryIds: prev.categoryIds.filter((i) => i !== id),
            }));
            setCommitted((prev) => ({
              ...prev,
              categoryIds: prev.categoryIds.filter((i) => i !== id),
            }));
          }
          break;
        case 'subcategory':
          if (id) {
            removeDraftSubcategory(id);
            setCommitted((prev) => ({
              ...prev,
              subcategoryIds: prev.subcategoryIds.filter((i) => i !== id),
            }));
          }
          break;
        case 'commission':
          setDraftCommissionRange(DEFAULT_COMMISSION_RANGE);
          setCommitted((prev) => ({
            ...prev,
            commissionRange: DEFAULT_COMMISSION_RANGE,
          }));
          break;
        case 'price':
          setDraftPriceRange(DEFAULT_PRICE_RANGE);
          setCommitted((prev) => ({
            ...prev,
            priceRange: DEFAULT_PRICE_RANGE,
          }));
          break;
        case 'discount':
          setDraftDiscountRange(DEFAULT_DISCOUNT_RANGE);
          setCommitted((prev) => ({
            ...prev,
            discountRange: DEFAULT_DISCOUNT_RANGE,
          }));
          break;
      }
    },
    [removeDraftSubcategory, setDraftCommissionRange, setDraftPriceRange, setDraftDiscountRange],
  );

  return {
    committed,
    draft,
    setDraftQuery,
    addDraftIndustry,
    removeDraftIndustry,
    addDraftCategory,
    removeDraftCategory,
    addDraftSubcategory,
    removeDraftSubcategory,
    setDraftCommissionRange,
    setDraftPriceRange,
    setDraftDiscountRange,
    cleanupInvalidCategories,
    cleanupInvalidSubcategories,
    commitAll,
    commitSearch,
    commitTaxonomy,
    clearDraft,
    clearAll,
    resetDraftToCommitted,
    hasDraftChanges,
    activeFilterCount,
    activePills,
    removePill,
  };
}

export default useDiscoveryFiltersState;
